import ScheduleEntry from '../models/ScheduleEntry';
import AcademicYearService from './AcademicYearService';

/**
 * Spatial index key for efficient lookups
 */
interface SpatialIndexKey {
  room: string;
  day: string;
  weekNumber: number;
  year: number;
}

/**
 * Spatial index for schedule items, optimized for fast lookups
 */
class ScheduleItemsIndex {
  private spatialIndex: Map<string, ScheduleEntry[]> = new Map();
  private academicYearService: AcademicYearService;

  constructor() {
    this.academicYearService = AcademicYearService.getInstance();
  }

  /**
   * Creates a spatial index key for efficient storage and retrieval
   */
  private createSpatialKey(room: string, day: string, weekNumber: number, year: number): string {
    return `${room}|${day}|${weekNumber}|${year}`;
  }

  /**
   * Builds the spatial index for a specific week and year
   * @param allScheduleItems - All schedule items to index
   * @param targetWeekNumber - The target week number
   * @param targetYear - The target year
   */
  async buildIndexForWeek(
    allScheduleItems: ScheduleEntry[],
    targetWeekNumber: number,
    targetYear: number
  ): Promise<void> {
    const startTime = performance.now();
    this.spatialIndex.clear();

    // Filter items that are active for the target week
    const activeItems = await this.filterActiveItems(allScheduleItems, targetWeekNumber, targetYear);
    
    // Group active items by room and day
    for (const item of activeItems) {
      if (!item.room || !item.day || !item.isTimeValid) {
        continue;
      }

      const key = this.createSpatialKey(item.room, item.day, targetWeekNumber, targetYear);
      
      if (!this.spatialIndex.has(key)) {
        this.spatialIndex.set(key, []);
      }
      
      this.spatialIndex.get(key)!.push(item);
    }

    const endTime = performance.now();
    console.log(`[ScheduleItemsIndex] Built spatial index for week ${targetWeekNumber}/${targetYear} in ${endTime - startTime}ms with ${activeItems.length} active items`);
  }

  /**
   * Builds the spatial index synchronously using cached academic year data
   * @param allScheduleItems - All schedule items to index
   * @param targetWeekNumber - The target week number
   * @param targetYear - The target year
   */
  buildIndexForWeekSync(
    allScheduleItems: ScheduleEntry[],
    targetWeekNumber: number,
    targetYear: number
  ): void {
    const startTime = performance.now();
    this.spatialIndex.clear();

    // Filter items that are active for the target week synchronously
    const activeItems = this.filterActiveItemsSync(allScheduleItems, targetWeekNumber, targetYear);
    
    // Group active items by room and day
    for (const item of activeItems) {
      if (!item.room || !item.day || !item.isTimeValid) {
        continue;
      }

      const key = this.createSpatialKey(item.room, item.day, targetWeekNumber, targetYear);
      
      if (!this.spatialIndex.has(key)) {
        this.spatialIndex.set(key, []);
      }
      
      this.spatialIndex.get(key)!.push(item);
    }

    const endTime = performance.now();
    console.log(`[ScheduleItemsIndex] Built spatial index for week ${targetWeekNumber}/${targetYear} in ${endTime - startTime}ms with ${activeItems.length} active items`);
  }

  /**
   * Filters schedule items to only include those active for the target week
   * @param allScheduleItems - All schedule items
   * @param targetWeekNumber - The target week number
   * @param targetYear - The target year
   * @returns Promise<ScheduleEntry[]> - Active items for the target week
   */
  private async filterActiveItems(
    allScheduleItems: ScheduleEntry[],
    targetWeekNumber: number,
    targetYear: number
  ): Promise<ScheduleEntry[]> {
    const activeItems: ScheduleEntry[] = [];
    
    for (const item of allScheduleItems) {
      if (!item) continue;
      
      const isActive = await item.isActiveForWeek(targetWeekNumber, targetYear);
      if (isActive) {
        activeItems.push(item);
      }
    }
    
    return activeItems;
  }

  /**
   * Filters schedule items to only include those active for the target week (synchronous version)
   * Uses cached academic year data for performance
   * @param allScheduleItems - All schedule items
   * @param targetWeekNumber - The target week number
   * @param targetYear - The target year
   * @returns ScheduleEntry[] - Active items for the target week
   */
  private filterActiveItemsSync(
    allScheduleItems: ScheduleEntry[],
    targetWeekNumber: number,
    targetYear: number
  ): ScheduleEntry[] {
    const activeItems: ScheduleEntry[] = [];
    
    for (const item of allScheduleItems) {
      if (!item) continue;
      
      const isActive = item.isActiveForWeekSync(targetWeekNumber, targetYear);
      if (isActive) {
        activeItems.push(item);
      }
    }
    
    return activeItems;
  }

  /**
   * Gets schedule items for a specific room and day from the spatial index
   * @param room - The room name
   * @param day - The day name
   * @param weekNumber - The week number
   * @param year - The year
   * @returns ScheduleEntry[] - Items in the specified room and day
   */
  getItemsForRoomAndDay(
    room: string,
    day: string,
    weekNumber: number,
    year: number
  ): ScheduleEntry[] {
    const key = this.createSpatialKey(room, day, weekNumber, year);
    return this.spatialIndex.get(key) || [];
  }

  /**
   * Checks if a room is occupied during a specific time slot
   * @param room - The room name
   * @param day - The day name
   * @param targetTimeMinutes - The target time in minutes
   * @param durationMinutes - The duration in minutes
   * @param excludeItemId - Item ID to exclude from the check
   * @param weekNumber - The week number
   * @param year - The year
   * @returns Object with occupancy info
   */
  checkRoomOccupancy(
    room: string,
    day: string,
    targetTimeMinutes: number,
    durationMinutes: number,
    excludeItemId: string,
    weekNumber: number,
    year: number
  ): { isOccupied: boolean; conflictingItem: ScheduleEntry | null } {
    const items = this.getItemsForRoomAndDay(room, day, weekNumber, year);
    
    if (items.length === 0) {
      return { isOccupied: false, conflictingItem: null };
    }

    const targetStart = targetTimeMinutes;
    const targetEnd = targetTimeMinutes + durationMinutes;

    for (const item of items) {
      if (item.id === excludeItemId) {
        continue;
      }

      const itemTime = item.parseTime();
      if (itemTime.startMinutes !== null && itemTime.endMinutes !== null) {
        if (targetStart < itemTime.endMinutes && targetEnd > itemTime.startMinutes) {
          return { isOccupied: true, conflictingItem: item };
        }
      }
    }

    return { isOccupied: false, conflictingItem: null };
  }

  /**
   * Checks if any group has a conflicting class at the same time
   * @param groups - The groups to check
   * @param day - The day name
   * @param targetTimeMinutes - The target time in minutes
   * @param durationMinutes - The duration in minutes
   * @param excludeItemId - Item ID to exclude from the check
   * @param weekNumber - The week number
   * @param year - The year
   * @returns Array of conflict reasons
   */
  checkGroupScheduleConflicts(
    groups: (string | number | { id: number; name?: string; group_code?: string; })[], 
    day: string,
    targetTimeMinutes: number,
    durationMinutes: number,
    excludeItemId: string,
    weekNumber: number,
    year: number
  ): Array<{ reason: string; severity: 'red' }> {
    const conflicts: Array<{ reason: string; severity: 'red' }> = [];
    
    if (!groups || groups.length === 0) {
      return conflicts;
    }

    const targetStart = targetTimeMinutes;
    const targetEnd = targetTimeMinutes + durationMinutes;

    // Check each group for conflicts across all rooms for this day
    for (const group of groups) {
      const groupIdentifier = typeof group === 'object' ? 
        ((group as any).name || (group as any).group_code || (group as any).id) : group;

      // Get all items for this day across all rooms
      const allItemsForDay = this.getAllItemsForDay(day, weekNumber, year);
      
      for (const item of allItemsForDay) {
        if (item.id === excludeItemId || !item.groups || item.groups.length === 0) {
          continue;
        }

        // Check if this item has the same group
        const hasConflictingGroup = item.groups.some(itemGroup => {
          const itemGroupName = typeof itemGroup === 'object' ? 
            ((itemGroup as any).name || (itemGroup as any).group_code || (itemGroup as any).id) : itemGroup;
          return itemGroupName === groupIdentifier;
        });

        if (hasConflictingGroup) {
          // Check for time overlap
          const itemTime = item.parseTime();
          if (itemTime.startMinutes !== null && itemTime.endMinutes !== null) {
            if (targetStart < itemTime.endMinutes && targetEnd > itemTime.startMinutes) {
              const conflictTime = item.timeRangeDisplay();
              const conflictCourse = item.course || item.type || 'another class';
              const conflictRoom = item.room || 'unknown room';
              
              conflicts.push({
                reason: `Group (${groupIdentifier}) has conflicting class: ${conflictCourse} in ${conflictRoom} ${conflictTime}, week ${weekNumber}`,
                severity: 'red'
              });
            }
          }
        }
      }
    }

    return conflicts;
  }

  /**
   * Checks if a teacher has a conflicting class at the same time
   * @param teacher - The teacher name
   * @param day - The day name
   * @param targetTimeMinutes - The target time in minutes
   * @param durationMinutes - The duration in minutes
   * @param excludeItemId - Item ID to exclude from the check
   * @param weekNumber - The week number
   * @param year - The year
   * @returns Array of conflict reasons
   */
  checkTeacherScheduleConflicts(
    teacher: string,
    day: string,
    targetTimeMinutes: number,
    durationMinutes: number,
    excludeItemId: string,
    weekNumber: number,
    year: number
  ): Array<{ reason: string; severity: 'red' }> {
    const conflicts: Array<{ reason: string; severity: 'red' }> = [];
    
    if (!teacher) {
      return conflicts;
    }

    const targetStart = targetTimeMinutes;
    const targetEnd = targetTimeMinutes + durationMinutes;

    // Get all items for this day across all rooms
    const allItemsForDay = this.getAllItemsForDay(day, weekNumber, year);
    
    for (const item of allItemsForDay) {
      if (item.id === excludeItemId) {
        continue;
      }

      if (!item.teacher || item.teacher !== teacher) {
        continue;
      }

      // Check for time overlap
      const itemTime = item.parseTime();
      if (itemTime.startMinutes !== null && itemTime.endMinutes !== null) {
        if (targetStart < itemTime.endMinutes && targetEnd > itemTime.startMinutes) {
          const conflictTime = item.timeRangeDisplay();
          const conflictCourse = item.course || item.type || 'another class';
          const conflictRoom = item.room || 'unknown room';
          
          conflicts.push({
            reason: `Teacher (${teacher}) has conflicting class: ${conflictCourse} in ${conflictRoom} ${conflictTime}, week ${weekNumber}`,
            severity: 'red'
          });
        }
      }
    }

    return conflicts;
  }

  /**
   * Gets all items for a specific day across all rooms
   * @param day - The day name
   * @param weekNumber - The week number
   * @param year - The year
   * @returns ScheduleEntry[] - All items for the day
   */
  private getAllItemsForDay(
    day: string,
    weekNumber: number,
    year: number
  ): ScheduleEntry[] {
    const allItems: ScheduleEntry[] = [];
    
    // Iterate through all keys in the spatial index to find items for this day
    this.spatialIndex.forEach((items, key) => {
      const [room, keyDay, keyWeek, keyYear] = key.split('|');
      
      if (keyDay === day && keyWeek === weekNumber.toString() && keyYear === year.toString()) {
        allItems.push(...items);
      }
    });
    
    return allItems;
  }

  /**
   * Clears the spatial index
   */
  clear(): void {
    this.spatialIndex.clear();
  }

  /**
   * Gets the size of the spatial index (for debugging)
   */
  getIndexSize(): number {
    let totalItems = 0;
    this.spatialIndex.forEach((items) => {
      totalItems += items.length;
    });
    return totalItems;
  }
}

export default ScheduleItemsIndex; 